import { useEffect, useLayoutEffect, useRef, useState } from "react";
import clsx from "clsx";
import { createPortal } from "react-dom";
import { useHoverCapable } from "@/hooks/useHoverCapable";
import type { PlanungAssignment } from "@/lib/montageplanung";
import { AssignmentDetailsContent } from "./AssignmentDetailsContent";
import {
  MP_TOOLTIP_SHOW_DELAY_MS,
  MP_TOOLTIP_Z_INDEX_CLASS,
} from "./layoutConstants";

interface AssignmentTooltipProps {
  assignment: PlanungAssignment;
  anchorRef: React.RefObject<HTMLElement | null>;
  pointerX?: number | null;
  visible: boolean;
  disabled?: boolean;
}

export function AssignmentTooltip({
  assignment,
  anchorRef,
  pointerX,
  visible,
  disabled,
}: AssignmentTooltipProps) {
  const hoverCapable = useHoverCapable();
  const [shown, setShown] = useState(false);
  const [position, setPosition] = useState({ top: 0, left: 0 });
  const panelRef = useRef<HTMLDivElement>(null);
  const timerRef = useRef<number | null>(null);

  useEffect(() => {
    if (timerRef.current) window.clearTimeout(timerRef.current);
    if (!hoverCapable || !visible || disabled) {
      setShown(false);
      return;
    }
    timerRef.current = window.setTimeout(() => setShown(true), MP_TOOLTIP_SHOW_DELAY_MS);
    return () => {
      if (timerRef.current) window.clearTimeout(timerRef.current);
    };
  }, [hoverCapable, visible, disabled, assignment.id]);

  useLayoutEffect(() => {
    if (!shown || !anchorRef.current || !panelRef.current) return;
    const anchor = anchorRef.current.getBoundingClientRect();
    const panel = panelRef.current.getBoundingClientRect();
    const pad = 8;
    const anchorX =
      pointerX != null
        ? Math.min(Math.max(pointerX, anchor.left), anchor.right)
        : anchor.left + anchor.width / 2;
    let left = anchorX - panel.width / 2;
    let top = anchor.top - panel.height - 10;
    if (left < pad) left = pad;
    if (left + panel.width > window.innerWidth - pad) {
      left = Math.max(pad, window.innerWidth - panel.width - pad);
    }
    if (top < pad) {
      top = anchor.bottom + 10;
    }
    setPosition({ top, left });
  }, [shown, anchorRef, assignment, pointerX]);

  if (!shown) return null;

  return createPortal(
    <div
      ref={panelRef}
      className={clsx(
        "mp-tooltip pointer-events-none fixed max-w-[min(92vw,24rem)] rounded-ios border border-separator bg-card p-3 shadow-ios-md ring-1 ring-black/5",
        MP_TOOLTIP_Z_INDEX_CLASS,
      )}
      style={{ top: position.top, left: position.left }}
      role="tooltip"
    >
      <AssignmentDetailsContent assignment={assignment} showProjectActions={false} />
    </div>,
    document.body,
  );
}
